import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldAlert, UserRoundCheck, ExternalLink, ShieldCheck, CreditCard, Mail } from "lucide-react";
import { toast } from "sonner";

// Calculate age from ISO date
const calcAge = (isoDate?: string | null) => {
  if (!isoDate) return null;
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age;
};

const PayoutsSetup = () => {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const mode = (params.get("mode") || "start") as "start" | "resume";

  const [dob, setDob] = useState<string | null>(null);
  const age = useMemo(() => calcAge(dob), [dob]);
  const isMinor = age !== null && age < 18;

  const [guardianEmail, setGuardianEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingDob, setLoadingDob] = useState(true);
  const [fallbackMinor, setFallbackMinor] = useState(false);
  const triedAdultOnceRef = useRef(false);

  // Fetch DOB immediately; do not ask user to input it here
  useEffect(() => {
    const fetchDob = async () => {
      if (!user) {
        setLoadingDob(false);
        return;
      }
      setLoadingDob(true);
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('date_of_birth')
          .eq('id', user.id)
          .single();
        if (error) throw error;
        setDob((data as any)?.date_of_birth ?? null);
      } catch (e) {
        // If we can't load DOB, proceed without it
        setDob(null);
      } finally {
        setLoadingDob(false);
      }
    };
    fetchDob();
  }, [user]);

  const startAdultOnboarding = async (silent = false) => {
    setLoading(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) throw new Error("Not authenticated");
      const { data, error } = await supabase.functions.invoke("connect-create-account", {
        headers: { Authorization: `Bearer ${sessionData.session.access_token}` },
      });
      if (error) throw error;
      const url = (data as any)?.url as string | undefined;
      if (!url) throw new Error("Missing onboarding URL");
      // Navigate in the same tab (this is the new tab we opened)
      window.location.replace(url);
    } catch (e: any) {
      const raw = JSON.stringify(e || {});
      const lower = raw.toLowerCase();
      if (raw.includes("MINOR_GUARDIAN_REQUIRED")) {
        // Expected control-flow for minors: silently show minor UI without an error toast
        setFallbackMinor(true);
      } else if (raw.includes("DOB_REQUIRED") || lower.includes("date_of_birth")) {
        // Missing DOB – guide user without an error toast
        setFallbackMinor(true);
        if (!silent) toast.message("Continue with a parent/guardian to finish payouts.");
      } else if (lower.includes("unauthorized") || lower.includes("authorization")) {
        if (!silent) toast.error("Please sign in and try again.");
      } else {
        const msg = e?.context?.error || e?.error || e?.message || "Could not start onboarding";
        // Avoid noisy generic errors on initial load
        if (!lower.includes("non-2xx")) {
          if (!silent) toast.error(msg);
        } else {
          // Non-2xx without details: suppress on initial auto attempt
          setFallbackMinor(true);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const startMinorOnboarding = async () => {
    setLoading(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) throw new Error("Not authenticated");
      if (!guardianEmail || !guardianEmail.includes("@")) throw new Error("Please enter a valid parent/guardian email");
      const { data, error } = await supabase.functions.invoke("connect-create-account", {
        headers: { Authorization: `Bearer ${sessionData.session.access_token}` },
        body: { guardian_email: guardianEmail },
      });
      if (error) throw error;
      const url = (data as any)?.url as string | undefined;
      if (!url) throw new Error("Missing onboarding URL");
      window.location.replace(url); // same tab
    } catch (e: any) {
      const raw = JSON.stringify(e || {});
      if (raw.toLowerCase().includes("unauthorized") || raw.toLowerCase().includes("authorization")) {
        toast.error("Please sign in and try again.");
      } else {
        const msg = e?.context?.error || e?.error || e?.message || "Could not start onboarding for minors";
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const resumeOnboarding = async () => {
    setLoading(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) throw new Error("Not authenticated");
      const { data, error } = await supabase.functions.invoke("connect-account-link", {
        headers: { Authorization: `Bearer ${sessionData.session.access_token}` },
      });
      if (error) throw error;
      const url = (data as any)?.url as string | undefined;
      if (!url) throw new Error("Missing onboarding URL");
      window.location.replace(url); // same tab
    } catch (e: any) {
      const raw = JSON.stringify(e || {});
      if (raw.toLowerCase().includes("unauthorized") || raw.toLowerCase().includes("authorization")) {
        toast.error("Please sign in and try again.");
      } else {
        const msg = e?.context?.error || e?.error || e?.message || "Could not resume onboarding";
        if (!raw.toLowerCase().includes("non-2xx")) {
          toast.error(msg);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // Auto behavior on load: fire immediately without waiting for DOB
  useEffect(() => {
    if (mode === "resume") {
      resumeOnboarding();
      return;
    }
    if (!triedAdultOnceRef.current) {
      triedAdultOnceRef.current = true;
      // Eliminates perceived blank time by starting onboarding right away
      startAdultOnboarding(true);
    }
    // DOB still loads in background to decide minor UI if needed
  }, [mode]);

  const showMinorUI = (isMinor === true) || fallbackMinor;

  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
      <Card className="w-full max-w-xl">
        <CardHeader>
          <CardTitle className="font-poppins text-2xl">Payouts setup</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {mode === "resume" ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Reopening Stripe onboarding…
            </div>
          ) : loadingDob ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Preparing payouts setup…
            </div>
          ) : showMinorUI ? (
            <div className="space-y-6">
              <div className="rounded-lg p-4 bg-gradient-to-r from-primary/10 to-accent/10 border border-border">
                <h3 className="font-poppins text-xl sm:text-2xl">You’re almost ready</h3>
                <p className="text-sm sm:text-base text-muted-foreground mt-1">
                  Because you’re under 18, payouts are set up with a parent or guardian for safety and compliance.
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                <div className="p-3 rounded-md bg-card border flex items-start gap-2">
                  <ShieldCheck className="h-5 w-5 text-primary" />
                  <div className="text-sm">
                    <p className="font-medium leading-none">Safe & compliant</p>
                    <p className="text-muted-foreground mt-1">Payouts go to a guardian’s account.</p>
                  </div>
                </div>
                <div className="p-3 rounded-md bg-card border flex items-start gap-2">
                  <Mail className="h-5 w-5 text-primary" />
                  <div className="text-sm">
                    <p className="font-medium leading-none">Quick setup</p>
                    <p className="text-muted-foreground mt-1">Use your guardian’s email to begin.</p>
                  </div>
                </div>
                <div className="p-3 rounded-md bg-card border flex items-start gap-2">
                  <CreditCard className="h-5 w-5 text-primary" />
                  <div className="text-sm">
                    <p className="font-medium leading-none">Secure payouts</p>
                    <p className="text-muted-foreground mt-1">They connect their bank details.</p>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border bg-muted/30 p-4">
                <div className="text-xs text-muted-foreground mb-2">What you’ll see in Stripe</div>
                <div className="space-y-2">
                  <div className="flex items-center gap-3 rounded-md bg-background border p-2">
                    <UserRoundCheck className="h-4 w-4" />
                    <div>
                      <div className="text-xs text-muted-foreground">Guardian full name</div>
                      <div className="text-sm font-medium">Taylor Morgan</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 rounded-md bg-background border p-2">
                    <Mail className="h-4 w-4" />
                    <div>
                      <div className="text-xs text-muted-foreground">Guardian email</div>
                      <div className="text-sm font-medium">taylor.parent@example.com</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 rounded-md bg-background border p-2">
                    <CreditCard className="h-4 w-4" />
                    <div>
                      <div className="text-xs text-muted-foreground">Payout destination</div>
                      <div className="text-sm font-medium">Bank account •••• 1234</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-base">Parent or guardian email</Label>
                <Input
                  type="email"
                  placeholder="guardian@email.com"
                  value={guardianEmail}
                  onChange={(e) => setGuardianEmail(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  We’ll use this email to open Stripe’s secure Connect setup for your parent/guardian.
                </p>
              </div>

              <Button onClick={startMinorOnboarding} disabled={loading} className="w-full">
                {loading ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Preparing Stripe…</>) : "Continue"}
              </Button>

              <p className="text-xs text-muted-foreground">
                Note: Where permitted by law, payouts for under‑18 users are routed to a parent/guardian’s account on their behalf.
              </p>

              {age === null && (
                <p className="text-xs text-muted-foreground text-center">
                  Don’t see your DOB on file? You can add it in your profile for a smoother setup.
                  <a href="/my-profile" className="inline-flex items-center gap-1 ml-1 underline">
                    Update profile <ExternalLink className="h-3 w-3" />
                  </a>
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <UserRoundCheck className="h-4 w-4" />
                Redirecting to Stripe Connect…
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Opening…
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PayoutsSetup;
